import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { toAbsoluteUrl } from '@/utils/Assets';
import { Skeleton } from '@/components/ui/skeleton';
import emailUsage from '@/services/setting/emailUsage';

const formatCount = (value) => (value ?? 0).toLocaleString();

const ExternalServicesIntegrations = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    emailUsage
      .getEmailUsage()
      .then(({ data }) => {
        if (active) setUsage(data);
      })
      .catch(() => {
        if (active) setUsage(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const percentUsed = Math.min(100, usage?.percent_used ?? 0);

  // Amber then red, so a firm sees the limit coming before a campaign is refused.
  const barColour =
    percentUsed >= 90 ? 'bg-danger' : percentUsed >= 75 ? 'bg-warning' : 'bg-primary';

  const renderStatusBadge = () => {
    if (loading) return <Skeleton className="w-[80px] h-[16px]" />;

    const connected = Boolean(usage?.configured);

    return (
      <span
        className={`badge badge-xs badge-pill badge-outline ${connected ? 'badge-success' : 'badge-light'}`}
      >
        {connected ? 'Connected' : 'Not set up'}
      </span>
    );
  };

  const renderUsage = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-2">
          <Skeleton className="w-[220px] h-[14px]" />
          <Skeleton className="w-full h-[8px]" />
        </div>
      );
    }

    if (!usage) {
      return (
        <span className="text-2sm text-gray-600">
          We could not load your email usage just now. Try refreshing the page.
        </span>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between flex-wrap gap-1">
          <span className="text-2sm text-gray-800">
            <span className="font-medium text-gray-900">{formatCount(usage.used_total)}</span> of{' '}
            {formatCount(usage.monthly_cap)} emails used this month
          </span>
          {usage.resets_on && (
            <span className="text-2sm text-gray-600">
              Resets on {format(parseISO(usage.resets_on), 'd MMM yyyy')}
            </span>
          )}
        </div>

        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${barColour}`}
            style={{ width: `${percentUsed}%` }}
          />
        </div>

        <span className="text-2sm text-gray-600">
          Campaigns can send {formatCount(usage.campaign_remaining)} more emails this month. The
          rest of your allowance is saved for emails the system sends, such as password resets and
          invoices, so those always go out.
        </span>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header" id="external_services_integrations">
        <h3 className="card-title">Integrations</h3>
      </div>

      <div className="card-body grid gap-5 lg:gap-7.5 lg:py-7.5 py-5">
        <div className="flex flex-col border border-gray-200 rounded-xl gap-4 p-3.5">
          <div className="flex items-center flex-wrap gap-3.5">
            <img
              src={toAbsoluteUrl('/media/brand-logos/sendgrid-icon.svg')}
              className="size-8 shrink-0"
              alt=""
            />

            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-900">SendGrid</span>
                {renderStatusBadge()}
              </div>
              <span className="text-2sm text-gray-700">
                Delivers your email campaigns and the emails the system sends. It is set up for you
                — there is nothing to connect here.
              </span>
            </div>
          </div>

          {renderUsage()}
        </div>
      </div>
    </div>
  );
};

export { ExternalServicesIntegrations };
